import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { Box } from "@mui/material";
import Loader from "@components/Loader/Loader";
import { useDeletePhotoMutation, useGetPhotosQuery } from "@store/photoApi";
import PhotoDrawer from "@components/Home/Photos/PhotoDrawer";
import Pictures from "@components/Pictures/Pictures";
import { Photo } from "@models/photo";
import PicturesUploadDrawer from "@components/Home/Photos/PicturesUploadDrawer";
import { useForm } from "react-hook-form";
import TextIconButton from "@components/TextIconButton";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ConfirmDialog from "@components/ConfirmDialog/ConfirmDialog";
import { setMessage } from "@store/snackbarSlice";
import { useDebounceValue } from "@hooks/useDebounceValue";
import HomePageTitle from "@components/Home/HomePageTitle/HomePageTitle";
import EmptyState from '@components/EmptyState/EmptyState';

const Photos: FC = () => {
  const { t } = useTranslation();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [checkedPhotos, setCheckedPhotos] = useState<Set<Photo>>(new Set());
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const searchForm = useForm<{ search: string }>();
  const searchStr = useDebounceValue(searchForm.watch("search"), 500);
  const { data: photos, isLoading } = useGetPhotosQuery(
    {
      patientId: selectedPatientId!,
      search: searchStr,
    },
    {
      skip: !selectedPatientId,
    },
  );
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);

  const [deletePhotoReq, { isLoading: isDeleting }] = useDeletePhotoMutation();
  const dispatch = useDispatch();

  const checkPhoto = useCallback((photo: Photo) => {
    setCheckedPhotos((photos: Set<Photo>) => {
      const newSet = new Set(photos);
      if (newSet.has(photo)) {
        newSet.delete(photo);
      } else {
        newSet.add(photo);
      }
      return newSet;
    });
  }, []);

  const deletePhotos = useCallback(
    async (photos: Photo[]): Promise<boolean> => {
      for (const photo of photos) {
        const { error } = await deletePhotoReq(photo.id);

        if (error) {
          return false;
        }
      }
      return true;
    },
    [deletePhotoReq],
  );

  const onConfirmDelete = useCallback(async () => {
    const photos = selectedPhoto ? [selectedPhoto] : Array.from(checkedPhotos);
    const success = await deletePhotos(photos);
    setConfirmDeleteDialogOpen(false);

    if (!success) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
      return;
    }

    dispatch(
      setMessage({
        content: t(`photos.photoDrawer.deletePhotoSuccessMessage`),
        severity: "success",
      }),
    );
    if (selectedPhoto) {
      setSelectedPhoto(null);
    } else {
      setCheckedPhotos(new Set());
    }
  }, [deletePhotos, selectedPhoto, checkedPhotos, t, dispatch]);

  const onPrevious = useCallback(() => {
    if (!photos?.data || !selectedPhoto) {
      return;
    }
    const index = photos.data.indexOf(selectedPhoto);
    const prevIndex = index > 0 ? index - 1 : photos.data.length - 1;
    setSelectedPhoto(photos.data[prevIndex]);
  }, [selectedPhoto, photos]);

  const onNext = useCallback(() => {
    if (!photos?.data || !selectedPhoto) {
      return;
    }
    const index = photos.data.indexOf(selectedPhoto);
    const nextIndex = photos.data.length - 1 > index ? index + 1 : 0;
    setSelectedPhoto(photos.data[nextIndex]);
  }, [selectedPhoto, photos]);

  return (
    <Box display="flex" flexDirection="column">
      <HomePageTitle
        title=""
        search={{
          placeholder: t("photos.searchInput.placeholder"),
          form: searchForm,
        }}
      />
      {!photos?.data || isLoading ? (
        <Loader />
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap="16px"
            marginBottom="32px"
          >
            <TextIconButton
              variant="outlined"
              color="error"
              text={t("photos.deleteButton")}
              icon={DeleteOutlineOutlinedIcon}
              onClick={() => setConfirmDeleteDialogOpen(true)}
              disabled={!checkedPhotos.size}
            />
            <TextIconButton
              variant="outlined"
              text={t("photos.addPicturesButton")}
              icon={AddOutlinedIcon}
              onClick={() => setUploadDialogOpen(true)}
            />
          </Box>

          {photos.data?.length ? (
            <Pictures
              pictures={photos?.data || []}
              onSelect={setSelectedPhoto}
              onCheck={checkPhoto}
              selectedPicture={selectedPhoto}
              checkedPictures={checkedPhotos}
            />
          ) : (
            <EmptyState image={`${process.env.PUBLIC_URL}/images/art-photos.jpeg`} label={t('photos.noData')}/>
          )}

          <PicturesUploadDrawer
            onClose={() => setUploadDialogOpen(false)}
            open={uploadDialogOpen}
          />

          <PhotoDrawer
            photoId={selectedPhoto?.id}
            onClose={() => setSelectedPhoto(null)}
            onDelete={() => setConfirmDeleteDialogOpen(true)}
            onPrevious={onPrevious}
            onNext={onNext}
          />

          <ConfirmDialog
            processing={isDeleting}
            open={confirmDeleteDialogOpen}
            onConfirm={onConfirmDelete}
            onCancel={() => setConfirmDeleteDialogOpen(false)}
          ></ConfirmDialog>
        </>
      )}
    </Box>
  );
};

export default Photos;
